import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Window, TextField, Button, Popup, PopupBody, LoadingOverlay, ValidationIndicator, type ValidationStatus } from '@warmancer/ui';
import { authApi, ApiClientError, getErrorMessage, DISCORD_URL, type LoginResponse } from '../api/client';
import { useAuthStore } from '../stores/authStore';
import { useFingerprint } from '../hooks/useFingerprint';
import styles from './AuthPages.module.css';

export function Login() {
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const { getFingerprint } = useFingerprint();

  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showUnverifiedPopup, setShowUnverifiedPopup] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  // Real-time login field validation
  const loginStatus = useMemo((): ValidationStatus => {
    if (!login) return 'idle';
    return login.trim().length > 0 ? 'valid' : 'invalid';
  }, [login]);

  // Real-time password validation
  const passwordStatus = useMemo((): ValidationStatus => {
    if (!password) return 'idle';
    return password.length > 0 ? 'valid' : 'invalid';
  }, [password]);

  const loginMutation = useMutation<LoginResponse, Error>({
    mutationFn: async () => {
      const fp = await getFingerprint();
      return authApi.login({ username_or_email: login, password, fp_request_id: fp.requestId });
    },
    onSuccess: (data: LoginResponse) => {
      setSession(data.session_token, data.account);
      navigate('/');
    },
    onError: (error: Error) => {
      if (error instanceof ApiClientError && error.code === 'AUTH_EMAIL_NOT_VERIFIED') {
        setShowUnverifiedPopup(true);
        return;
      }
      const message = getErrorMessage(error);
      if (error instanceof ApiClientError && error.field) {
        setErrors({ [error.field]: message });
      } else {
        setErrors({ form: message });
      }
    },
  });

  const resendMutation = useMutation({
    mutationFn: async () => {
      return authApi.resendVerificationPublic({ email: login });
    },
    onSuccess: () => {
      setResendSuccess(true);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!login.trim()) {
      setErrors({ login: 'Name or email is required' });
      return;
    }
    if (!password) {
      setErrors({ password: 'Password is required' });
      return;
    }

    loginMutation.mutate();
  };

  const clearFormError = () => {
    setErrors((prev) => {
      const { form, ...rest } = prev;
      return rest;
    });
  };

  return (
    <>
      <Window title="Welcome to Warmancer" maxWidth={400} className={styles.window}>
        <form onSubmit={handleSubmit} className={styles.form}>
          <TextField
            name="login"
            label="Name or Email"
            value={login}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setLogin(e.target.value);
              if (errors.login) setErrors((prev) => { const { login: _, ...rest } = prev; return rest; });
            }}
            error={!!errors.login}
            autoComplete="username"
            autoFocus
            tabIndex={1}
            rightElement={<ValidationIndicator status={errors.login ? 'invalid' : loginStatus} message={errors.login || (loginStatus === 'invalid' ? 'Name or email is required' : '')} />}
          />

          <TextField
            name="password"
            type="password"
            label="Password"
            value={password}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setPassword(e.target.value);
              if (errors.password) setErrors((prev) => { const { password: _, ...rest } = prev; return rest; });
            }}
            error={!!errors.password}
            autoComplete="current-password"
            tabIndex={2}
            rightElement={<ValidationIndicator status={errors.password ? 'invalid' : passwordStatus} message={errors.password || (passwordStatus === 'invalid' ? 'Password is required' : '')} />}
          />

          <Button
            type="submit"
            disabled={loginMutation.isPending}
            tabIndex={3}
          >
            Login
          </Button>

          <Button
            type="button"
            onClick={() => navigate('/register')}
            tabIndex={4}
          >
            Create Account
          </Button>

          <Button
            type="button"
            onClick={() => navigate('/forgot-password')}
            tabIndex={5}
          >
            Forgot Password
          </Button>

          <a
            href={DISCORD_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.discordButton}
            tabIndex={6}
          >
            <svg className={styles.discordIcon} viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
            </svg>
            Join our Discord
          </a>

          <div className={styles.buildVersion}>
            v2 - {__BUILD_TIME__}
          </div>
        </form>
      </Window>

      <Popup
        open={!!errors.form}
        title="Login Failed"
        variant="error"
      >
        <PopupBody
          note={<>Join us on <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer">Discord</a> for help</>}
          buttons={<Button onClick={clearFormError}>Try Again</Button>}
        >
          {errors.form?.split('\n').map((line, i) => <p key={i}>{line}</p>)}
        </PopupBody>
      </Popup>

      <Popup
        open={showUnverifiedPopup}
        title="Email Not Verified"
        variant="error"
      >
        <PopupBody
          note={<>Email sent from contact@warmancer.com<br />Join us on <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer">Discord</a> for help</>}
          buttons={<>
            <Button
              onClick={() => resendMutation.mutate()}
              disabled={resendMutation.isPending || resendSuccess}
            >
              {resendSuccess ? 'Email Sent!' : 'Resend Email'}
            </Button>
            <Button onClick={() => setShowUnverifiedPopup(false)}>
              Close
            </Button>
          </>}
        >
          <p>Please verify your email address before logging in.</p>
          <p>Check your inbox <strong>and spam folder</strong> for a verification link.</p>
        </PopupBody>
      </Popup>

      <LoadingOverlay
        visible={loginMutation.isPending || resendMutation.isPending}
        message={resendMutation.isPending ? 'Sending verification email...' : 'Logging in...'}
        timeout={10000}
      />
    </>
  );
}
